/**
 * summaryUnavailable.js — R22 · PROD-165 (Dr. Rami 2026-10-03) + D451 (2026-10-04), patient P1
 * step 3. A summary built without the AI shows the D451 patient note and no warning signs.
 *
 * ★★★ THE DEFECT. When the pre-visit summary could not be generated, the API stored a
 *   hardcoded fallback with FOUR generic red flags, TWO at urgency 'high', identical for every
 *   patient; the patient summary is built from that pre-visit summary and handed its flags on.
 *
 * ★★ THE CONTRACT (orella-api D5b step 1, 78b50a1): get_fallback_pre_visit_summary returns
 *   `redFlags: []` and ONE machine key, `summaryUnavailable: true`, read by both clients.
 *   Rows stored before D5b carry no key but are the same template: every template the API ever
 *   produced carries the pair `model: 'fallback'` + `isAIGenerated: false`, so that pair counts
 *   too (the md panel's rule, orella-md-panel src/utils/summaryUnavailable.js, md8, kept
 *   identical by hand). A real AI result carries isAIGenerated true and is never hidden.
 * ★ STRICT ON PURPOSE: only the boolean `true`, or exactly that pair. A string "true", a 1,
 *   `model: 'fallback'` alone or `isAIGenerated: false` alone is an ordinary summary.
 *
 * ★★ BOTH OBJECTS ARE READ. The patient summary is generated FROM the case's pre-visit summary
 *   (api _build_patient_summary: an existing pre-visit summary with three or more conditions is
 *   used as is), and on D5b the patient summary built over the fallback carries no key of its
 *   own. So the screen asks the patient summary it shows, and the case's pre_visit_summary —
 *   the latter only when the flags on screen are that summary's flags (summaryUnavailableFor).
 *
 * ★ THE NOTE is D451's patient wording, in en.json as viewSummary.summaryUnavailableNote
 *   (English decided; Spanish waits for Dr. Rami; the nine coming-soon locales read English, D454).
 *
 * Pure (no imports). scripts/check-summary-unavailable.mjs executes it.
 */
const asObject = (v) => {
  if (typeof v === 'string') {
    try { return JSON.parse(v) } catch { return null }
  }
  return v
}

/** True when this stored summary was built without the AI (the md8 rule). */
export function isSummaryUnavailable(summary) {
  const s = asObject(summary)
  if (!s || typeof s !== 'object') return false
  if (s.summaryUnavailable === true) return true
  return s.model === 'fallback' && s.isAIGenerated === false
}

// warningSigns.js signKey, restated so this file stays import-free (gates load it as data).
const findingKey = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/[.!;:,]+$/, '').trim()
const findingSet = (flags) => new Set((Array.isArray(flags) ? flags : [])
  .map((f) => findingKey(typeof f === 'string' ? f : f && f.finding)).filter(Boolean))

/** True when two red-flag lists name the same findings (signKey-folded, order ignored). */
export function sameFindings(a, b) {
  const A = findingSet(a), B = findingSet(b)
  return A.size === B.size && [...A].every((k) => B.has(k))
}

/**
 * The screen's question: was what the patient is shown built without the AI?
 *   - nothing shown yet (the stream is still running, no summary object) -> no;
 *   - the shown patient summary says so itself -> yes;
 *   - ★ review r1 B1: the case's pre-visit summary is the template ONLY COUNTS when the flags on
 *     screen came from it — the same findings (the D5b template has none, so a patient summary
 *     over it shows none; a pre-D5b summary copied the generic four). A real patient summary's
 *     real flags stay visible even when the pre-visit summary was LATER replaced by the
 *     template (the MD panel's regenerate or the prewarm failing after the patient summary was
 *     cached).
 * `shownFlags` defaults to the patient summary's own redFlags.
 */
export function summaryUnavailableFor(patientSummary, preVisitSummary, shownFlags) {
  const ps = asObject(patientSummary)
  if (!ps || typeof ps !== 'object') return false
  if (isSummaryUnavailable(ps)) return true
  const pvs = asObject(preVisitSummary)
  if (!isSummaryUnavailable(pvs)) return false
  return sameFindings(shownFlags === undefined ? ps.redFlags : shownFlags, pvs.redFlags)
}
